// SSE "signal" channel for a deal (`GET /deals/:dealId/events`).
//
// The stream does NOT push domain payloads. Each event is a tiny
// notification telling the client *what changed*, and the client re-reads the
// canonical state. The wire format is the shared source of truth in
// `lib/deal-events.types.ts` (identical on the backend):
//
//   { "kind": "SYNC" }                                 // reload everything
//   { "kind": "DEAL" }                                 // refetch the deal
//   { "kind": "ITEMS", "action": "itemsUpdated",
//     "itemIds": [...], "deletedItemIds": [...] }        // reload / drop the named items
//
// The low-level SSE framing (chunk buffering, `data:` extraction, heartbeat
// comment stripping) is shared with the deal-items stream in
// `lib/streaming/sse.ts`. This module only owns the domain parsing below.

import {
  DEAL_ITEMS_ACTION_TYPES,
  type DealChangeNotification,
  type DealItemsActionType,
} from '@/lib/deal-events.types';

function isDealItemsActionType(value: unknown): value is DealItemsActionType {
  return (
    typeof value === 'string' &&
    (DEAL_ITEMS_ACTION_TYPES as readonly string[]).includes(value)
  );
}

/**
 * Parse an SSE frame's `data:` payload into a typed {@link DealChangeNotification}.
 *
 * Returns `null` (rather than throwing) for anything unexpected — malformed
 * JSON, a non-object body, an unknown `kind`, or an `ITEMS` signal without a
 * valid `action` — so a single bad frame never tears down the stream. Dropping
 * an odd frame is safe: the next reconnect emits a `SYNC` that reloads
 * everything.
 */
function parseItemIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (id): id is string => typeof id === 'string' && id.length > 0
  );
}

export function parseDealEvent(raw: string): DealChangeNotification | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!data || typeof data !== 'object') return null;

  const { kind, action, itemIds, deletedItemIds } = data as {
    kind?: unknown;
    action?: unknown;
    itemIds?: unknown;
    deletedItemIds?: unknown;
  };

  if (kind === 'SYNC') return { kind: 'SYNC' };
  if (kind === 'DEAL') return { kind: 'DEAL' };
  if (kind === 'ITEMS' && isDealItemsActionType(action)) {
    // Absent or malformed reads as "not named" — the same as the empty lists the server sends
    // when it will not name them, so an older server keeps triggering a full reload.
    return {
      kind: 'ITEMS',
      action,
      itemIds: parseItemIds(itemIds),
      deletedItemIds: parseItemIds(deletedItemIds),
    };
  }
  return null;
}
